import {
  PRODUCT_ASPECT_TYPES,
  SHADOW_TYPES,
  DIRECTIONS,
  PLACEMENT_TYPES,
  BOX_ASPECT_RATIO_MIN,
  BOX_ASPECT_RATIO_MAX,
} from '../constants/positions'
import type { AspectCounts } from '../constants/placementStrategies'
import { FabricImage } from 'fabric'
import type { PlacementResult } from '@/types/placementResult'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'

export interface ProductData {
  id: string
  url: string
  width: number
  height: number
  aspectType?: string
  isMultiple?: boolean
}

interface ShadowOptions {
  blur?: number
  color?: string
  lightDirection?: string
  placement?: string
  [key: string]: any
}

/**
 * Classifies the aspect ratio of a product based on its dimensions
 */
export function classifyAspectRatio(width: number, height: number): string {
  const ratio = width / height

  if (ratio > BOX_ASPECT_RATIO_MIN && ratio <= BOX_ASPECT_RATIO_MAX) {
    return PRODUCT_ASPECT_TYPES.BOX_HORIZONTAL
  }

  if (1 / ratio > BOX_ASPECT_RATIO_MIN && 1 / ratio <= BOX_ASPECT_RATIO_MAX) {
    return PRODUCT_ASPECT_TYPES.BOX_VERTICAL
  }

  if (ratio > BOX_ASPECT_RATIO_MAX) {
    return PRODUCT_ASPECT_TYPES.HORIZONTAL
  }

  if (1 / ratio > BOX_ASPECT_RATIO_MAX) {
    return PRODUCT_ASPECT_TYPES.VERTICAL
  }

  return PRODUCT_ASPECT_TYPES.SQUARE
}

/**
 * Counts the occurrences of each aspect type in the products array
 */
export function countAspects(productsData: ProductData[]): AspectCounts {
  const counts: AspectCounts = {
    [PRODUCT_ASPECT_TYPES.BOX_HORIZONTAL]: 0,
    [PRODUCT_ASPECT_TYPES.BOX_VERTICAL]: 0,
    [PRODUCT_ASPECT_TYPES.HORIZONTAL]: 0,
    [PRODUCT_ASPECT_TYPES.VERTICAL]: 0,
    [PRODUCT_ASPECT_TYPES.SQUARE]: 0,
  }
  productsData.forEach((p) => {
    if (p.aspectType) {
      counts[p.aspectType]++
    }
  })
  return counts
}

/**
 * Creates a descriptive summary of the product configuration
 */
export function getConfigurationDescription(
  productsData: ProductData[],
  aspectCounts: AspectCounts,
): string {
  if (productsData.length === 0) return 'N/A'
  const aspectSummary = Object.entries(aspectCounts)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => `${count} ${type}`)
    .join(', ')
  return `${productsData.length} Product(s): ${aspectSummary}`
}

/**
 * Determines available placements based on product count and aspect ratios
 * Uses config store for config-driven placement rules
 */
export function determineAvailablePlacements(
  productCount: number,
  aspectCounts: AspectCounts,
  productData: ProductData[],
): string[] {
  // Use config store to get available placements with condition evaluation
  const adSizeStore = useAdSizeConfigStore()
  let availablePlacements = adSizeStore.getAvailablePlacements(productCount, aspectCounts)

  if (availablePlacements.length === 0) {
    return []
  }

  // Eliminate staircase placement if products aren't suitable
  if (availablePlacements.includes('staircase') && !shouldUseStaircase(productData)) {
    availablePlacements = availablePlacements.filter((p) => p !== 'staircase')
  }

  // Eliminate other placements except front, layer and staircase if dual-tone background is used
  return availablePlacements
}

/**
 * Determines whether 'staircase' placement should be used based on product size variation.
 * @param products - Array of product dimension data
 * @returns true if staircase placement should be allowed; false otherwise
 */
function shouldUseStaircase(products: ProductData[]): boolean {
  if (products.length < 2) return false
  // For 2 Products
  if (products.length === 2) {
    const [p1, p2] = products

    const widthDiff = Math.abs(p1.width - p2.width) / Math.max(p1.width, p2.width)
    const heightDiff = Math.abs(p1.height - p2.height) / Math.max(p1.height, p2.height)

    const isTooSimilar = widthDiff < 0.1 && heightDiff < 0.1
    const isTooDifferent = widthDiff > 0.75 || heightDiff > 0.75

    if (!isTooSimilar && !isTooDifferent) {
      if (widthDiff >= 0.15 && widthDiff <= 0.4 && heightDiff >= 0.15 && heightDiff <= 0.4) {
        return true // Use staircase
      }
    }
  }
  // For 3 Products
  if (products.length == 3) {
    let isValid = true
    // Step 1: Sort products by height (ascending)
    const sorted = [...products].sort((a, b) => a.height - b.height)
    for (let i = 0; i < 2; i++) {
      const height1 = sorted[i].height
      const height2 = sorted[i + 1].height
      const maxHeight = Math.max(height1, height2)
      const diffRatio = Math.abs(height1 - height2) / maxHeight
      if (diffRatio < 0.1 || diffRatio > 0.9) {
        isValid = false
        break
      }
    }
    return isValid
  }
  return false
}

/**
 * Selects a random element from an array
 */
export function selectRandomElement<T>(arr: T[]): T | null {
  if (!arr || arr.length === 0) return null
  return arr[Math.floor(Math.random() * arr.length)]
}

/**
 * Calculates shadow options based on shadow type and product
 * This provides configuration options compatible with shadowHelper.ts
 */
export function calculateShadowOptions(
  shadowType: string,
  product: ProductData | undefined,
  placement: string | undefined = undefined,
): ShadowOptions {
  const options: ShadowOptions = {
    // Default to front light direction
    lightDirection: DIRECTIONS.FRONT,
  }

  if (!product) return options // No product to base dimensions on

  // If we have a placement, include it in the options
  if (placement) {
    options.placement = placement
  }

  // For GLOW shadow, provide a color
  if (shadowType === SHADOW_TYPES.GLOW) {
    options.color = `rgba(0,0,0,0.8)` // Default dark color for glow
  }

  // Select a light direction appropriate for the shadow type
  const lightDirections = getLightDirectionsForShadowType(shadowType)
  options.lightDirection = selectRandomElement(lightDirections) || DIRECTIONS.FRONT

  return options
}

/**
 * Gets appropriate light directions for different shadow types
 */
export function getLightDirectionsForShadowType(shadowType: string): string[] {
  switch (shadowType) {
    case SHADOW_TYPES.DROP:
      return [DIRECTIONS.TOP_LEFT, DIRECTIONS.TOP_RIGHT, DIRECTIONS.BOTTOM_LEFT]
    case SHADOW_TYPES.CONTACT:
      return [DIRECTIONS.TOP, DIRECTIONS.LEFT, DIRECTIONS.RIGHT, DIRECTIONS.BOTTOM]
    case SHADOW_TYPES.AMBIENT:
    case SHADOW_TYPES.GLOW:
    default:
      return Object.values(DIRECTIONS)
  }
}

/**
 * Selects a optimal element from an array
 */
export function selectOptimalElement(
  arr: string[],
  aspectCounts: AspectCounts,
  productsLength: number,
): string {
  const optimalPlacements: string[] = []

  if (
    (productsLength === 3 && aspectCounts?.Horizontal === 3) ||
    (productsLength === 2 && aspectCounts?.Horizontal === 2)
  ) {
    optimalPlacements.push(PLACEMENT_TYPES.CORNER)
  } else if (productsLength === 2 && aspectCounts?.Vertical === 2) {
    optimalPlacements.push(PLACEMENT_TYPES.CORNER)
  } else if (productsLength === 2 && aspectCounts?.Horizontal === 2) {
    optimalPlacements.push(PLACEMENT_TYPES.CORNER)
  }
  else if (aspectCounts?.Vertical === 3) {
    optimalPlacements.push(PLACEMENT_TYPES.FRONT)
  } else if (
    productsLength === 2 &&
    (aspectCounts?.Square === 2 || aspectCounts?.BoxHorizontal === 2)
  ) {
    optimalPlacements.push(PLACEMENT_TYPES.STACK)
  }

  for (const type of optimalPlacements) {
    if (arr.includes(type)) return type
  }

  for (const type of priorityOrder) {
    if (arr.includes(type)) return type
  }
  return arr[0]
}

const priorityOrder = [PLACEMENT_TYPES.LAYER, PLACEMENT_TYPES.STAIRCASE, PLACEMENT_TYPES.FRONT]

export function getLightDirectionsForPlacementType(placement: string): string[] {
  switch (placement) {
    case PLACEMENT_TYPES.FRONT:
      return [DIRECTIONS.NONE, DIRECTIONS.TOP, DIRECTIONS.LEFT, DIRECTIONS.RIGHT]
    case PLACEMENT_TYPES.TOP:
      return [DIRECTIONS.NONE, DIRECTIONS.TOP_LEFT, DIRECTIONS.TOP_RIGHT, DIRECTIONS.BOTTOM_LEFT]
    case PLACEMENT_TYPES.FLOAT:
      return [
        DIRECTIONS.NONE,
        DIRECTIONS.FRONT,
        DIRECTIONS.TOP,
        DIRECTIONS.LEFT,
        DIRECTIONS.RIGHT,
        DIRECTIONS.BEHIND,
      ]
    case PLACEMENT_TYPES.CORNER:
      return [DIRECTIONS.NONE, DIRECTIONS.TOP_RIGHT, DIRECTIONS.BEHIND]
    // Disabled
    // case PLACEMENT_TYPES.VERTICAL:
    //   return [DIRECTIONS.NONE, DIRECTIONS.FRONT, DIRECTIONS.TOP, DIRECTIONS.BEHIND]
    // case PLACEMENT_TYPES.PYRAMID:
    //   return [DIRECTIONS.NONE, DIRECTIONS.TOP, DIRECTIONS.LEFT, DIRECTIONS.RIGHT]
    // case PLACEMENT_TYPES.COLLAGE1:
    // case PLACEMENT_TYPES.COLLAGE2:
    // case PLACEMENT_TYPES.COLLAGE3:
    case PLACEMENT_TYPES.STACK:
      return [DIRECTIONS.NONE, DIRECTIONS.TOP_LEFT, DIRECTIONS.TOP_RIGHT, DIRECTIONS.BOTTOM_LEFT]
    default:
      return [DIRECTIONS.NONE, DIRECTIONS.TOP_LEFT, DIRECTIONS.TOP_RIGHT, DIRECTIONS.BOTTOM_LEFT]
  }
}

/**
 * Loads a product image as a fabric.js object and trims it
 */
export function loadProductAsFabricImage(productData: ProductData): Promise<FabricImage> {
  return new Promise(async (resolve, reject) => {
    try {
      // If the product data already has a trimmed URL (from getProductDimensions),
      // we can load it directly without re-trimming
      const img = await FabricImage.fromURL(productData.url, { crossOrigin: 'anonymous' })
      try {
        if (!img || !(img as any)._element) {
          resolve(img)
          return
        }

        // Set initial metadata on the fabric image
        img.set({
          id: productData.id,
          originalWidth: productData.width,
          originalHeight: productData.height,
          aspectType: productData.aspectType,
        })

        resolve(img)
      } catch (err) {
        console.error('Error processing fabric image:', err)
        resolve(img)
      }
    } catch (err) {
      console.error('Error loading fabric image:', err)
      reject(err)
    }
  })
}

/**
 * Selects the most appropriate main shadow type based on the placement and product aspects
 */
function selectAppropriateMainShadow(placement: string): string {
  const normalizedPlacement = placement.toLowerCase()

  switch (normalizedPlacement) {
    case PLACEMENT_TYPES.FRONT.toLowerCase():
      return SHADOW_TYPES.CONTACT
    case PLACEMENT_TYPES.TOP.toLowerCase():
      return SHADOW_TYPES.DROP
    case PLACEMENT_TYPES.FLOAT.toLowerCase():
      return SHADOW_TYPES.GLOW
    case PLACEMENT_TYPES.CORNER.toLowerCase():
      return SHADOW_TYPES.DROP
    case PLACEMENT_TYPES.STACK.toLowerCase():
    // Disabled
    // case PLACEMENT_TYPES.VERTICAL.toLowerCase():
    //   return aspectCounts[PRODUCT_ASPECT_TYPES.VERTICAL] > 0
    //     ? SHADOW_TYPES.AMBIENT
    //     : SHADOW_TYPES.DROP
    // case PLACEMENT_TYPES.COLLAGE1.toLowerCase():
    // case PLACEMENT_TYPES.COLLAGE2.toLowerCase():
    // case PLACEMENT_TYPES.COLLAGE3.toLowerCase():
    // case PLACEMENT_TYPES.PYRAMID.toLowerCase():
    //   return SHADOW_TYPES.DROP
    default:
      return SHADOW_TYPES.DROP
  }
}

/**
 * Suggests the optimal product placement configuration based on selected products
 * @param productsData Array of selected product data
 * @param includeFabricImages Whether to include trimmed fabric images in the result
 * @returns An object with suggested placement and shadow information
 */
export async function suggestOptimalPlacement(
  productsData: ProductData[],
  availablePlacements: string[],
  includeFabricImages: boolean = true,
): Promise<PlacementResult> {
  if (!productsData || productsData.length === 0) {
    throw new Error('No products provided for placement suggestion')
  }

  // Ensure all products have aspect type classified
  productsData.forEach((product) => {
    if (!product.aspectType) {
      product.aspectType = classifyAspectRatio(product.width, product.height)
    }
  })

  // Generate placement strategy
  const aspectCounts = countAspects(productsData)

  const selectedPlacement =
    selectOptimalElement(availablePlacements, aspectCounts, productsData.length) ||
    PLACEMENT_TYPES.FRONT
  const mainShadow = selectAppropriateMainShadow(selectedPlacement)
  const supportedLightDirections = getLightDirectionsForPlacementType(selectedPlacement).filter(
    (dir) => dir !== DIRECTIONS.NONE,
  )
  const lightDirection =
    supportedLightDirections.length > 0 ? supportedLightDirections[0] : DIRECTIONS.TOP_LEFT

  // Create result object
  const result = {
    placement: selectedPlacement,
    mainShadow,
    lightDirection,
    description: `${getConfigurationDescription(productsData, aspectCounts)}\nSuggested placement: ${selectedPlacement}\nMain shadow: ${mainShadow}`,
    aspectCounts,
  }

  // Return result immediately if fabric images aren't needed
  if (!includeFabricImages) {
    return result
  }

  // Load fabric images if requested
  const fabricImages = await Promise.all(
    productsData.map((product) => loadProductAsFabricImage(product)),
  )

  return {
    ...result,
    fabricImages,
  }
}

// Add trimImage helper
export function trimImage(img: FabricImage): Promise<FabricImage> {
  return new Promise(async (resolve) => {
    const imgElement = img.getElement() as HTMLImageElement
    const tempCanvas = document.createElement('canvas')
    const ctx = tempCanvas.getContext('2d')
    if (!ctx) return
    const fullWidth = imgElement.naturalWidth
    const fullHeight = imgElement.naturalHeight

    // Shrink by 5px on each side (left, right, top, bottom)
    const cropX = 5
    const cropY = 5
    const cropWidth = fullWidth - 10
    const cropHeight = fullHeight - 10

    tempCanvas.width = cropWidth
    tempCanvas.height = cropHeight

    // Draw the image with 5px crop from all sides
    ctx.drawImage(imgElement, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight)

    // Get only inner cropped pixels
    const imageData = ctx.getImageData(0, 0, tempCanvas.width, tempCanvas.height)
    const pixels = imageData.data
    let minX = tempCanvas.width,
      minY = tempCanvas.height,
      maxX = 0,
      maxY = 0
    for (let y = 0; y < tempCanvas.height; y++) {
      for (let x = 0; x < tempCanvas.width; x++) {
        const index = (y * tempCanvas.width + x) * 4
        if (pixels[index + 3] > 10) {
          minX = Math.min(minX, x)
          minY = Math.min(minY, y)
          maxX = Math.max(maxX, x)
          maxY = Math.max(maxY, y)
        }
      }
    }
    const width = maxX - minX + 1
    const height = maxY - minY + 1
    const croppedCanvas = document.createElement('canvas')
    const croppedCtx = croppedCanvas.getContext('2d')
    if (!croppedCtx) return
    croppedCanvas.width = width
    croppedCanvas.height = height
    croppedCtx.drawImage(tempCanvas, minX, minY, width, height, 0, 0, width, height)
    const trimmedImg = await FabricImage.fromURL(croppedCanvas.toDataURL(), {
      crossOrigin: 'anonymous',
    })
    resolve(trimmedImg)
  })
}

// Update getProductDimensions to create a FabricImage first, then trim it, and use the trimmed image for dimensions
export async function getProductDimensions(
  url: string,
  index: number,
): Promise<{ productData: ProductData; fabricImage: FabricImage | null }> {
  const fabricImg = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' })
  const trimmedImg = await trimImage(fabricImg)
  const width = trimmedImg.width * trimmedImg.scaleX
  const height = trimmedImg.height * trimmedImg.scaleY
  const aspectType = classifyAspectRatio(width, height)

  trimmedImg.set({
    aspectType,
  })

  return {
    productData: {
      url,
      width,
      height,
      id: `P${index + 1}_${width}x${height}`,
      aspectType
    },
    fabricImage: trimmedImg,
  }
}
